import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import { LinkButton, Counter } from 'fronton-react';
import { useRecoilState } from 'recoil';
import { SMALL_FALLBACK_PRODUCT_IMAGE_URL } from 'pages/common/components/Product/fallback-product-images.const';
import { gaImpressionsProduct } from 'ga-events/ga-impressions-product';
import { dimensionsAtom, TComplimentaryProductInfo } from 'pages/dimensions/store/dimensions';
import { addNewCountValue } from 'common/helpers/add-new-count-value.helper';
import { getIsCheckedProducts } from 'common/helpers/get-is-checked-products.helper';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { ProductArticle } from '../ProductArticle';
import { ComplimentaryBlockPrice } from './components/ComplimentaryBlockPrice';
import { getImageSize } from '../helpers';
import {
  ComplimentaryProductImg,
  ComplimentaryProductInfoBlock,
  ComplimentaryProductInfoTitle,
  CounterWrapper,
  ComplementaryProductInfoWrapper,
  ComplimentaryProductInfoColumn,
  ComplimentaryProductInfoRow,
  ComplimentaryItemLink,
} from '../styles';
import { Checkbox } from '../Checkbox';

type TComplementaryProductComponent = {
  product: TComplimentaryProductInfo;
  index: number;
  isDisabled: boolean;
};

export const ComplementaryProductComponent = ({
  product,
  index,
  isDisabled,
}: TComplementaryProductComponent) => {
  const productRef = useRef<HTMLDivElement>(null);
  const { mobile } = useBreakpoints() ?? {};
  const [counter, setCounter] = useState<number>(
    product.total.requiredQuantity >= 1 ? product.total.requiredQuantity : 0
  );

  const [dimensionState, setDimensionState] = useRecoilState(dimensionsAtom);

  const imageSize = getImageSize(mobile);

  const handleOpenProduct = () => {
    window.open(`/product/${product.product_page_name.value[0]}`);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          gaImpressionsProduct({
            name: product.displayedName.value[0],
            id: product.productId,
            price: `${product.salesPriceValueInConsumerUnitValue ?? 0}`,
            position: `${index + 2}`,
          });
        }
      },

      {
        threshold: 0.5,
      }
    );

    if (productRef.current) {
      observer.observe(productRef.current);
    }

    return () => {
      if (productRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(productRef.current);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Нужно послать только один раз
  }, []);

  const addCount = (e: ChangeEvent<HTMLInputElement>, value: string) => {
    setDimensionState(prevState => ({
      ...prevState,
      complimentaryProductInfo: addNewCountValue({ prevState, newValue: Number(value), product }),
    }));
    setCounter(Number(value));
  };

  const isDisable = !getIsCheckedProducts({
    checkedProducts: dimensionState.checkedProducts,
    productId: product.productId,
  });

  return (
    <ComplimentaryProductInfoBlock ref={productRef}>
      <ComplimentaryProductInfoTitle>
        <Checkbox productId={product.productId} />
        <ComplimentaryProductImg
          onClick={handleOpenProduct}
          src={product?.mediaMainPhoto.value[0] || SMALL_FALLBACK_PRODUCT_IMAGE_URL}
          alt={product.displayedName.value[0]}
          loading="lazy"
          width={imageSize}
          height={imageSize}
        />
        <ComplimentaryProductInfoColumn>
          <ComplimentaryProductInfoRow>
            <ComplementaryProductInfoWrapper>
              <ProductArticle productId={product.productId} />
              <LinkButton
                disabled={isDisabled}
                className={ComplimentaryItemLink}
                style={{}}
                size="m"
                href={`/product/${product.product_page_name.value[0]}`}
                variant="normal">
                {product.displayedName.value[0]}
              </LinkButton>
            </ComplementaryProductInfoWrapper>

            <ComplimentaryBlockPrice isDisable={isDisable} counter={counter} product={product} />
          </ComplimentaryProductInfoRow>
          <CounterWrapper>
            <Counter min={1} onChange={addCount} value={`${counter}`} size="s" disabled={isDisable} />
          </CounterWrapper>
        </ComplimentaryProductInfoColumn>
      </ComplimentaryProductInfoTitle>
    </ComplimentaryProductInfoBlock>
  );
};
